import { FEATURE_FLAG_VALUES } from "./constants";
import {
  FeatureFlagChange,
  FeatureFlagInput,
  FeatureFlags,
  FeatureFlagSource,
  FEATURE_FLAGS,
  PartialFeatureFlags,
  TFeatureFlag,
} from "./types";

function buildFeatureFlags(
  getFlagValue: (feature: FEATURE_FLAGS) => boolean,
): FeatureFlags {
  return {
    [FEATURE_FLAGS.AUDIO_NOTES]: getFlagValue(FEATURE_FLAGS.AUDIO_NOTES),
    [FEATURE_FLAGS.ALLOW_ALL_USERS_TO_ADD_COMMENTS]: getFlagValue(
      FEATURE_FLAGS.ALLOW_ALL_USERS_TO_ADD_COMMENTS,
    ),
    [FEATURE_FLAGS.DOCUMENTS]: getFlagValue(FEATURE_FLAGS.DOCUMENTS),
    [FEATURE_FLAGS.DOCUMENT_ACKNOWLEDGMENTS]: getFlagValue(
      FEATURE_FLAGS.DOCUMENT_ACKNOWLEDGMENTS,
    ),
    [FEATURE_FLAGS.JHA]: getFlagValue(FEATURE_FLAGS.JHA),
    [FEATURE_FLAGS.CORRECTIVE_ACTIONS]: getFlagValue(
      FEATURE_FLAGS.CORRECTIVE_ACTIONS,
    ),
    [FEATURE_FLAGS.FORMS]: getFlagValue(FEATURE_FLAGS.FORMS),
    [FEATURE_FLAGS.STANDARDS]: getFlagValue(FEATURE_FLAGS.STANDARDS),
    [FEATURE_FLAGS.CONTRACTOR_MANAGEMENT]: getFlagValue(
      FEATURE_FLAGS.CONTRACTOR_MANAGEMENT,
    ),
    [FEATURE_FLAGS.EQUIPMENT_MANAGEMENT]: getFlagValue(
      FEATURE_FLAGS.EQUIPMENT_MANAGEMENT,
    ),
    [FEATURE_FLAGS.TRAINING_CERT]: getFlagValue(FEATURE_FLAGS.TRAINING_CERT),
    [FEATURE_FLAGS.SITE_MANAGEMENT]: getFlagValue(
      FEATURE_FLAGS.SITE_MANAGEMENT,
    ),
    [FEATURE_FLAGS.PEOPLE_MANAGEMENT]: getFlagValue(
      FEATURE_FLAGS.PEOPLE_MANAGEMENT,
    ),
    [FEATURE_FLAGS.DIVISION_MANAGEMENT]: getFlagValue(
      FEATURE_FLAGS.DIVISION_MANAGEMENT,
    ),
    [FEATURE_FLAGS.TO_DO_LIST]: getFlagValue(FEATURE_FLAGS.TO_DO_LIST),
    [FEATURE_FLAGS.PUBLIC_REGISTRATION]: getFlagValue(
      FEATURE_FLAGS.PUBLIC_REGISTRATION,
    ),
    [FEATURE_FLAGS.PUBLIC_SHARE_EQUIPMENT]: getFlagValue(
      FEATURE_FLAGS.PUBLIC_SHARE_EQUIPMENT,
    ),
    [FEATURE_FLAGS.PUBLIC_SHARE_CONTRACTORS]: getFlagValue(
      FEATURE_FLAGS.PUBLIC_SHARE_CONTRACTORS,
    ),
    [FEATURE_FLAGS.PUBLIC_SHARE_DOCUMENTS]: getFlagValue(
      FEATURE_FLAGS.PUBLIC_SHARE_DOCUMENTS,
    ),
    [FEATURE_FLAGS.PUBLIC_SHARE_JOBS]: getFlagValue(
      FEATURE_FLAGS.PUBLIC_SHARE_JOBS,
    ),
    [FEATURE_FLAGS.REPORTS]: getFlagValue(FEATURE_FLAGS.REPORTS),
    [FEATURE_FLAGS.SYSTEM_EXPORT]: getFlagValue(FEATURE_FLAGS.SYSTEM_EXPORT),
    [FEATURE_FLAGS.DEV_ONLY]: getFlagValue(FEATURE_FLAGS.DEV_ONLY),
    [FEATURE_FLAGS.TASKS]: getFlagValue(FEATURE_FLAGS.TASKS),
    [FEATURE_FLAGS.QUEUES]: getFlagValue(FEATURE_FLAGS.QUEUES),
    [FEATURE_FLAGS.NOTIFICATIONS]: getFlagValue(FEATURE_FLAGS.NOTIFICATIONS),
    [FEATURE_FLAGS.DOCUMENTS_OFFLINE]: getFlagValue(
      FEATURE_FLAGS.DOCUMENTS_OFFLINE,
    ),
    [FEATURE_FLAGS.FORMS_OFFLINE]: getFlagValue(FEATURE_FLAGS.FORMS_OFFLINE),
    [FEATURE_FLAGS.VIDEO_MANAGEMENT]: getFlagValue(
      FEATURE_FLAGS.VIDEO_MANAGEMENT,
    ),
    [FEATURE_FLAGS.DASHBOARD]: getFlagValue(FEATURE_FLAGS.DASHBOARD),
    [FEATURE_FLAGS.SCHEDULER]: getFlagValue(FEATURE_FLAGS.SCHEDULER),
    [FEATURE_FLAGS.LANGUAGE]: getFlagValue(FEATURE_FLAGS.LANGUAGE),
    [FEATURE_FLAGS.NONLINEAR_WORKFLOW]: getFlagValue(
      FEATURE_FLAGS.NONLINEAR_WORKFLOW,
    ),
    [FEATURE_FLAGS.WORKFLOW_ROUTING]: getFlagValue(
      FEATURE_FLAGS.WORKFLOW_ROUTING,
    ),
    [FEATURE_FLAGS.NONLINEAR_TRAINING]: getFlagValue(
      FEATURE_FLAGS.NONLINEAR_TRAINING,
    ),
    [FEATURE_FLAGS.PROJECT_MANAGEMENT]: getFlagValue(
      FEATURE_FLAGS.PROJECT_MANAGEMENT,
    ),
    [FEATURE_FLAGS.WEBHOOKS]: getFlagValue(FEATURE_FLAGS.WEBHOOKS),
    [FEATURE_FLAGS.PIN_MAP]: getFlagValue(FEATURE_FLAGS.PIN_MAP),
    [FEATURE_FLAGS.CLIMATE_API]: getFlagValue(FEATURE_FLAGS.CLIMATE_API),
    [FEATURE_FLAGS.DISABLE_BASIC_USER_TRAINING_SELF_MANAGEMENT]: getFlagValue(
      FEATURE_FLAGS.DISABLE_BASIC_USER_TRAINING_SELF_MANAGEMENT,
    ),
    [FEATURE_FLAGS.DISABLE_FORM_DUPLICATION]: getFlagValue(
      FEATURE_FLAGS.DISABLE_FORM_DUPLICATION,
    ),
  };
}

export function featureFlagName(feature: FeatureFlagInput): FEATURE_FLAGS {
  if (typeof feature === "string") {
    return feature;
  }

  return feature.name;
}

export function isFeatureFlagName(feature: string): feature is FEATURE_FLAGS {
  return FEATURE_FLAG_VALUES.some((flag) => flag === feature);
}

export function createEmptyFeatureFlags(): FeatureFlags {
  return buildFeatureFlags(() => false);
}

export function normalizeFeatureFlags(
  featureFlags?: PartialFeatureFlags,
): FeatureFlags {
  return buildFeatureFlags((feature) => featureFlags?.[feature] === true);
}

export function createFeatureFlagsFromList(
  features: TFeatureFlag[],
): FeatureFlags {
  const featureFlags = createEmptyFeatureFlags();

  features.forEach((feature) => {
    featureFlags[feature.name] = true;
  });

  return featureFlags;
}

/**
 * @todo this gets removed when featureFlags property is migrated and added to every client
 */
export function createFeatureFlagsFromSource(
  source?: FeatureFlagSource,
): FeatureFlags {
  if (source?.featureFlags) {
    return normalizeFeatureFlags(source.featureFlags);
  }

  return createFeatureFlagsFromList(source?.features || []);
}

export function mIGRATION_createFeatureFlagsFromSource(
  source?: FeatureFlagSource,
): FeatureFlags {
  return createFeatureFlagsFromSource(source);
}

export function applyFeatureFlagChanges(
  currentFeatureFlags: PartialFeatureFlags | undefined,
  featureChanges: FeatureFlagChange[],
): FeatureFlags {
  const nextFeatureFlags = normalizeFeatureFlags(currentFeatureFlags);

  featureChanges.forEach(({ feature, enable }) => {
    if (isFeatureFlagName(feature)) {
      nextFeatureFlags[feature] = enable;
    }
  });

  return nextFeatureFlags;
}

export function resolveFeatureFlags({
  availableFlags,
  defaultOnFlags,
  targetFlags,
}: {
  availableFlags: FeatureFlags;
  defaultOnFlags: FeatureFlags;
  targetFlags?: PartialFeatureFlags;
}): FeatureFlags {
  return buildFeatureFlags((feature) => {
    return (
      availableFlags[feature] === true &&
      (defaultOnFlags[feature] === true || targetFlags?.[feature] === true)
    );
  });
}

export function featureIsEnabled(
  enabledFlags: PartialFeatureFlags,
  testFlag: FeatureFlagInput,
): boolean {
  return enabledFlags[featureFlagName(testFlag)] === true;
}
